import { useState } from 'react';
import { DataTable } from '@striker/ui-shell';
import { apiFetch } from '../auth/session';

export type InvoiceRow = {
  id: number;
  stripe_invoice_id: string;
  status: string | null;
  amount_due: number;
  amount_euros: number;
  currency: string;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
  period_start: string | null;
  period_end: string | null;
};

type Props = {
  invoices: InvoiceRow[];
  /** Base path for refresh: tenant `/api/v1/tenant/billing/invoices` or master `/api/v1/master/tenants/:id/invoices` */
  refreshBasePath: string;
  onRefreshed?: (invoice: InvoiceRow) => void;
  emptyMessage?: string;
};

export function StripeInvoicesTable({
  invoices,
  refreshBasePath,
  onRefreshed,
  emptyMessage = 'Nessuna fattura Stripe (comparirà dopo i webhook invoice.*)',
}: Props) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function openLink(row: InvoiceRow, kind: 'pdf' | 'hosted') {
    setBusyId(row.stripe_invoice_id);
    setError(null);
    try {
      let pdf = row.invoice_pdf;
      let hosted = row.hosted_invoice_url;
      if (!pdf && !hosted) {
        const res = await apiFetch(
          `${refreshBasePath}/${encodeURIComponent(row.stripe_invoice_id)}/refresh`,
          { method: 'POST' },
        );
        const body = (await res.json().catch(() => ({}))) as {
          invoice?: InvoiceRow;
          message?: string;
          error?: string;
        };
        if (!res.ok) throw new Error(body.message || body.error || `refresh ${res.status}`);
        if (body.invoice) {
          pdf = body.invoice.invoice_pdf;
          hosted = body.invoice.hosted_invoice_url;
          onRefreshed?.(body.invoice);
        }
      }
      const url = kind === 'pdf' ? pdf : hosted;
      if (!url) {
        // one more refresh attempt if the preferred kind is missing
        const res = await apiFetch(
          `${refreshBasePath}/${encodeURIComponent(row.stripe_invoice_id)}/refresh`,
          { method: 'POST' },
        );
        const body = (await res.json().catch(() => ({}))) as {
          invoice?: InvoiceRow;
          message?: string;
          error?: string;
          offline?: boolean;
        };
        if (!res.ok) throw new Error(body.message || body.error || `refresh ${res.status}`);
        if (body.invoice) {
          onRefreshed?.(body.invoice);
          const next = kind === 'pdf' ? body.invoice.invoice_pdf : body.invoice.hosted_invoice_url;
          if (next) {
            window.open(next, '_blank', 'noopener,noreferrer');
            return;
          }
          if (body.offline) {
            throw new Error('Link non disponibile offline (serve STRIPE_SECRET_KEY per refresh)');
          }
        }
        throw new Error('Link Stripe non disponibile');
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  const rows = invoices.map((inv) => ({ ...inv, id: inv.id }));

  return (
    <div>
      {error ? <p className="sk-error">{error}</p> : null}
      <DataTable
        emptyMessage={emptyMessage}
        rows={rows}
        columns={[
          {
            key: 'amount_euros',
            label: 'Importo',
            render: (v, row) =>
              `€${Number(v).toFixed(2)} ${String(row.currency || 'eur').toUpperCase()}`,
          },
          {
            key: 'status',
            label: 'Stato',
            render: (v) => String(v || '—'),
          },
          {
            key: 'period_start',
            label: 'Periodo',
            render: (_v, row) => {
              const a = row.period_start
                ? new Date(row.period_start).toLocaleDateString('it-IT')
                : '—';
              const b = row.period_end
                ? new Date(row.period_end).toLocaleDateString('it-IT')
                : '—';
              return `${a} → ${b}`;
            },
          },
          {
            key: 'stripe_invoice_id',
            label: 'Links',
            render: (_v, row) => (
              <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                <button
                  type="button"
                  className="sk-btn sk-btn--quiet"
                  disabled={busyId === row.stripe_invoice_id}
                  onClick={() => void openLink(row, 'pdf')}
                >
                  Apri PDF
                </button>
                <button
                  type="button"
                  className="sk-btn sk-btn--quiet"
                  disabled={busyId === row.stripe_invoice_id}
                  onClick={() => void openLink(row, 'hosted')}
                >
                  Apri hosted
                </button>
              </span>
            ),
          },
        ]}
      />
    </div>
  );
}
